<template>
  <v-card class="variant-card mb-4" outlined>
    <v-card-text class="py-0">
      <div class="variant-card__header">
        <v-spacer />
        <v-btn
          v-if="isEditing && !isNew"
          icon
          small
          :disabled="isDisabled"
          :title="$t('miscellaneous.delete')"
          @click="$emit('remove')">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
        <v-btn
          v-if="showResetButton"
          icon
          small
          class="variant-card__reset"
          :disabled="isDisabled"
          @click="resetVariant">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-form ref="form" lazy-validation>
        <v-row dense>
          <v-col cols="12" md="2">
            <div class="image-upload-wrapper full-width">
              <label
                class="image-upload-box"
                :class="{
                  'is-disabled': isDisabled || isUploadingImage || !isEditing,
                  'has-image': localVariant.image || imagePreview,
                }">
                <input
                  type="file"
                  accept="image/*"
                  :disabled="isDisabled || isUploadingImage || !isEditing"
                  @change="onImageSelected" />
                <v-img
                  v-if="localVariant.image || imagePreview"
                  :src="imagePreview || localVariant.image"
                  width="100%"
                  height="100%"
                  cover></v-img>
                <v-progress-circular
                  v-else-if="isUploadingImage"
                  indeterminate
                  color="primary" />
                <v-icon v-else color="primary">mdi-image-plus</v-icon>
              </label>
            </div>
          </v-col>
          <v-col cols="12" md="10">
            <v-row dense>
              <v-col cols="12" :md="nameColumnMdColspan">
                <v-text-field
                  :value="localVariant.name"
                  :label="addAsterisk($t('generalAttributes.name'))"
                  :rules="[requiredRule]"
                  outlined
                  dense
                  :disabled="isDisabled || !isEditing"
                  @input="updateField('name', $event)" />
              </v-col>
              <v-col v-if="isCoinsCategory" cols="12" md="3">
                <v-select
                  :items="coinTypeOptions"
                  :value="coinType"
                  :label="
                    addAsterisk($t('entities.products.attributes.coinType'))
                  "
                  :rules="[requiredRule]"
                  outlined
                  dense
                  :disabled="isDisabled || !isEditing"
                  @change="coinType = $event" />
              </v-col>
              <v-col v-if="isCoinsCategory" cols="12" md="3">
                <v-text-field
                  :value="coinAmount"
                  type="number"
                  step="1"
                  min="0"
                  :label="
                    addAsterisk($t('entities.products.attributes.coinAmount'))
                  "
                  :rules="amountRules"
                  outlined
                  dense
                  :disabled="isCoinAmountFieldDisabled"
                  @input="coinAmount = $event">
                  <template #prepend-inner>
                    <CoinIcon :size="18" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col v-if="isPassesCategory" cols="12" md="3">
                <v-menu
                  v-model="passValidFromMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      :value="passValidFrom"
                      :label="
                        addAsterisk(
                          $t('entities.products.attributes.validFrom'),
                        )
                      "
                      :rules="[requiredRule]"
                      outlined
                      dense
                      readonly
                      :disabled="isDisabled || !isEditing"
                      v-on="on" />
                  </template>
                  <v-time-picker
                    v-model="passValidFrom"
                    format="24hr"
                    @input="passValidFromMenu = false" />
                </v-menu>
              </v-col>
              <v-col v-if="isPassesCategory" cols="12" md="3">
                <v-menu
                  v-model="passValidToMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-bind="attrs"
                      :value="passValidTo"
                      :label="
                        addAsterisk($t('entities.products.attributes.validTo'))
                      "
                      :rules="[requiredRule]"
                      outlined
                      dense
                      readonly
                      :disabled="isDisabled || !isEditing"
                      v-on="on" />
                  </template>
                  <v-time-picker
                    v-model="passValidTo"
                    format="24hr"
                    @input="passValidToMenu = false" />
                </v-menu>
              </v-col>
            </v-row>
            <v-row dense class="mt-0">
              <v-col cols="12" :md="descriptionColWidth">
                <v-textarea
                  :value="localVariant.description"
                  :label="$t('generalAttributes.description')"
                  rows="1"
                  outlined
                  dense
                  :disabled="isDisabled || !isEditing"
                  @input="updateField('description', $event)" />
              </v-col>
              <v-col v-if="isCoinsPack" cols="12" md="3">
                <v-select
                  :items="packTypeOptions"
                  :value="localVariant.pack_type"
                  :label="$t('entities.products.attributes.packType')"
                  outlined
                  dense
                  :disabled="isDisabled || !isEditing"
                  @change="updateField('pack_type', $event)" />
              </v-col>
              <v-col v-if="isPassesCategory" cols="12" md="6">
                <v-select
                  :items="weekdayOptions"
                  :value="passWeekdays"
                  :label="
                    addAsterisk($t('entities.products.attributes.weekdays'))
                  "
                  :rules="[requiredRule]"
                  multiple
                  outlined
                  dense
                  :disabled="isDisabled || !isEditing"
                  @change="passWeekdays = $event" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { COIN_TYPES, COIN_TYPE_LABELS } from '@/constants/coins'
import { PACK_TYPE_OPTIONS } from '@/constants/packs'
import { WEEKDAYS } from '@/constants/weekdays'
import CoinIcon from '@/components/CoinIcon.vue'
import { mapActions } from 'vuex'
import FilesApi from '@/services/misc/FilesApi'

const resetUpdates = (variant) => {
  const updates = {
    name: variant.name || '',
    description: variant.description || null,
    image: variant.image || null,
    sort_order: variant.sort_order || 1,
    attributes: variant.attributes || {},
    pack_type: variant.pack_type || null,
  }
  if (variant.id) updates.id = variant.id
  return updates
}

export default {
  name: 'VariantUpsertCard',
  components: {
    CoinIcon,
  },
  props: {
    isDisabled: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: true },
    isNew: { type: Boolean, default: false },
    isCoinsCategory: { type: Boolean, default: false },
    isPassesCategory: { type: Boolean, default: false },
    original: { type: Object, default: () => ({}) },
    value: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      localVariant: resetUpdates(this.value),
      imagePreview: '',
      imageFile: null,
      isUploadingImage: false,
      passValidFromMenu: false,
      passValidToMenu: false,
    }
  },
  computed: {
    requiredRule() {
      return (value) =>
        (Array.isArray(value)
          ? value.length > 0
          : typeof value === 'string'
          ? value.trim().length > 0
          : value !== null && value !== undefined && value !== '') ||
        this.$t('validation.required')
    },
    amountRules() {
      if (this.coinType === COIN_TYPES.coins) return []
      return [this.requiredRule, this.integerRule]
    },
    integerRule() {
      return (value) => {
        if (value === '' || value === null || value === undefined) {
          return this.$t('validation.required')
        }
        const parsed = Number(value)
        return Number.isInteger(parsed) || this.$t('validation.integer')
      }
    },
    isCoinAmountFieldDisabled() {
      return (
        this.isDisabled || !this.isEditing || this.coinType === COIN_TYPES.coins
      )
    },
    nameColumnMdColspan() {
      return this.isCoinsCategory || this.isPassesCategory ? 6 : 12
    },
    showResetButton() {
      if (!this.isEditing) return false
      if (this.isNew) return true
      const current = this.normalizeVariant(this.localVariant)
      const original = this.normalizeVariant(resetUpdates(this.original))
      return JSON.stringify(current) !== JSON.stringify(original)
    },
    coinTypeOptions() {
      const coinTypes = Object.keys(COIN_TYPE_LABELS)
      return coinTypes.map((type) => ({
        text: COIN_TYPE_LABELS[type],
        value: type,
      }))
    },
    packTypeOptions() {
      return PACK_TYPE_OPTIONS
    },
    coinType: {
      get() {
        return this.localVariant?.attributes?.type || COIN_TYPES.coins
      },
      set(value) {
        this.setCoinAttributes(value, this.coinAmount)
      },
    },
    coinAmount: {
      get() {
        const amount = this.localVariant?.attributes?.amount
        return amount === null || amount === undefined ? '' : amount
      },
      set(value) {
        const parsed = this.parseInteger(value)
        this.setCoinAttributes(this.coinType, parsed)
      },
    },
    weekdayOptions() {
      return WEEKDAYS.map((weekday) => ({
        text: this.$t(`miscellaneous.days.${weekday.name}`),
        value: weekday.value,
      }))
    },
    passValidFrom: {
      get() {
        return this.localVariant?.attributes?.valid_from || ''
      },
      set(value) {
        this.setPassAttributes(value, this.passValidTo, this.passWeekdays)
      },
    },
    passValidTo: {
      get() {
        return this.localVariant?.attributes?.valid_to || ''
      },
      set(value) {
        this.setPassAttributes(this.passValidFrom, value, this.passWeekdays)
      },
    },
    passWeekdays: {
      get() {
        const weekdays = this.localVariant?.attributes?.weekdays
        return Array.isArray(weekdays) ? weekdays : []
      },
      set(value) {
        this.setPassAttributes(this.passValidFrom, this.passValidTo, value)
      },
    },
    isCoinsPack() {
      return this.isCoinsCategory && this.coinType === COIN_TYPES.coins_pack
    },
    descriptionColWidth() {
      if (this.isPassesCategory) return 6
      if (this.isCoinsPack) return 9
      return 12
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    updateField(field, value) {
      if (!this.isEditing) return
      this.localVariant = {
        ...this.localVariant,
        [field]: value,
      }
      this.emitUpdate()
      this.emitChange()
    },
    emitUpdate() {
      this.$emit('input', { ...this.localVariant })
    },
    emitChange() {
      this.$emit('change')
    },
    addAsterisk(label) {
      return `${label} *`
    },
    async onImageSelected(event) {
      const [file] = event.target.files || []
      if (!file) {
        this.imageFile = null
        this.imagePreview = ''
        return
      }
      if (!this.isEditing) return
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
      await this.uploadSelectedImage()
    },
    async uploadSelectedImage() {
      if (!this.imageFile) {
        return
      }
      this.isUploadingImage = true
      try {
        const response = await FilesApi.upload('arenas', this.imageFile)
        this.imageFile = null
        if (!response || response.status !== 200 || !response.data?.link) {
          this.notifyUploadError()
          return
        }
        this.imagePreview = ''
        this.updateField('image', response.data.link)
      } catch (error) {
        console.error(error)
        this.notifyUploadError()
      } finally {
        this.isUploadingImage = false
      }
    },
    notifyUploadError() {
      this.showSnackbar({
        color: 'error',
        message: this.$t('miscellaneous.fileUploadFailed'),
      })
    },
    resetVariant() {
      if (this.isDisabled) return
      if (this.isNew) {
        this.$emit('remove')
        return
      }
      const next = resetUpdates(this.original)
      if (this.localVariant.sort_order && !next.sort_order) {
        next.sort_order = this.localVariant.sort_order
      }
      this.localVariant = next
      this.imagePreview = ''
      this.imageFile = null
      this.emitUpdate()
      this.emitChange()
    },
    normalizeVariant(variant) {
      const normalizeAmount = (amount) => {
        if (amount === '' || amount === null || amount === undefined) {
          return ''
        }
        const parsed = Number(amount)
        return Number.isNaN(parsed) ? '' : Math.trunc(parsed)
      }
      const attributes = variant?.attributes
      const normalizedAttributes =
        attributes &&
        typeof attributes === 'object' &&
        !Array.isArray(attributes)
          ? Object.keys(attributes)
              .sort()
              .reduce((acc, key) => {
                acc[key] = attributes[key]
                return acc
              }, {})
          : {}
      if ('amount' in normalizedAttributes) {
        normalizedAttributes.amount = normalizeAmount(
          normalizedAttributes.amount,
        )
      }
      if (Array.isArray(normalizedAttributes.weekdays)) {
        normalizedAttributes.weekdays = [...normalizedAttributes.weekdays]
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value))
          .sort((a, b) => a - b)
      }
      return {
        id: variant?.id || '',
        name: variant?.name ? variant.name.trim() : '',
        description: variant?.description ? variant.description.trim() : null,
        image: variant?.image ? variant.image.trim() : null,
        sort_order:
          typeof variant?.sort_order === 'number' ? variant.sort_order : 1,
        attributes: normalizedAttributes,
        pack_type: variant?.pack_type || null,
      }
    },
    ensureCoinAttributes() {
      const currentType = this.localVariant?.attributes?.type
      const currentAmount = this.localVariant?.attributes?.amount
      const desiredType = currentType || COIN_TYPES.coins
      this.setCoinAttributes(desiredType, currentAmount ?? '')
    },
    stripCoinAttributes() {
      const attributes = { ...(this.localVariant?.attributes || {}) }
      let changed = false
      if ('type' in attributes) {
        delete attributes.type
        changed = true
      }
      if ('amount' in attributes) {
        delete attributes.amount
        changed = true
      }
      if (changed) {
        this.updateField('attributes', attributes)
      }
    },
    setCoinAttributes(type, amount) {
      if (!this.isCoinsCategory) {
        return
      }
      const attributes = { ...(this.localVariant?.attributes || {}) }
      delete attributes.type
      delete attributes.amount
      if (type) {
        attributes.type = type
      }
      if (type !== COIN_TYPES.coins && amount !== '' && amount !== null) {
        attributes.amount = amount
      }
      this.updateField('attributes', attributes)
    },
    parseInteger(value) {
      if (value === '' || value === null || value === undefined) {
        return ''
      }
      const parsed = parseInt(value, 10)
      return Number.isNaN(parsed) ? '' : parsed
    },
    ensurePassAttributes() {
      const currentFrom = this.localVariant?.attributes?.valid_from || ''
      const currentTo = this.localVariant?.attributes?.valid_to || ''
      const currentWeekdays = this.localVariant?.attributes?.weekdays || []
      this.setPassAttributes(currentFrom, currentTo, currentWeekdays)
    },
    stripPassAttributes() {
      const attributes = { ...(this.localVariant?.attributes || {}) }
      let changed = false
      if ('valid_from' in attributes) {
        delete attributes.valid_from
        changed = true
      }
      if ('valid_to' in attributes) {
        delete attributes.valid_to
        changed = true
      }
      if ('weekdays' in attributes) {
        delete attributes.weekdays
        changed = true
      }
      if (changed) {
        this.updateField('attributes', attributes)
      }
    },
    setPassAttributes(validFrom, validTo, weekdays) {
      if (!this.isPassesCategory) {
        return
      }
      const attributes = { ...(this.localVariant?.attributes || {}) }
      delete attributes.valid_from
      delete attributes.valid_to
      delete attributes.weekdays
      if (validFrom !== undefined) {
        attributes.valid_from = validFrom
      }
      if (validTo !== undefined) {
        attributes.valid_to = validTo
      }
      if (Array.isArray(weekdays)) {
        attributes.weekdays = weekdays
      }
      this.updateField('attributes', attributes)
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.localVariant = resetUpdates(newValue)
        this.imagePreview = ''
        this.imageFile = null
      },
    },
    isCoinsCategory: {
      immediate: true,
      handler(isCoins) {
        if (isCoins) {
          this.ensureCoinAttributes()
        } else {
          this.stripCoinAttributes()
        }
      },
    },
    isPassesCategory: {
      immediate: true,
      handler(isPasses) {
        if (isPasses) {
          this.ensurePassAttributes()
        } else {
          this.stripPassAttributes()
        }
      },
    },
    isEditing(value) {
      if (value) {
        if (this.isCoinsCategory) {
          this.ensureCoinAttributes()
        }
        if (this.isPassesCategory) {
          this.ensurePassAttributes()
        }
      }
    },
  },
}
</script>

<style scoped>
.variant-card {
  border-color: #e0e0e0;
}

.variant-card__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 28px;
  margin: 0.25rem 0;
}

.variant-card__reset {
  align-self: center;
}

.v-select ::v-deep .v-text-field__details {
  margin-top: 0;
}

.image-upload-wrapper.full-width {
  width: 100%;
  height: auto;
}

.image-upload-box {
  position: relative;
  width: 100%;
  height: 110px;
  border: 1px dashed var(--v-primary-base);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

.image-upload-box.has-image {
  border: none;
}

.image-upload-box input[type='file'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.image-upload-box.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
